import { h } from '@omni-torn/ui';
import { ACCOUNT_LEVELS, KEY_USES, poolText, TERMS_VERSION, type FactionPoolView, KEY_USES_LIMIT_NOTE, showConnectCode, TERMS_FULL_LABEL, TERMS_READ_MORE, termsAnchor, TORN_TERMS } from '@omni-torn/shared';
import { server, session } from '../session';
import { box } from '../ui';

/**
 * Your key in Omni-Torn — the same everywhere (website, extension, Discord bot /account): its uses,
 * each switched on or off here (server: apps/api player-keys.ts, /api/me/keys), a code that signs
 * another browser in, and removing it everywhere. Torn's terms table right above the choices.
 */

interface KeysInfo {
  account: { enabled: boolean; on: boolean; tracked: boolean; status: string; accessLevel: number | null; problem: string | null };
  faction: { factionId: number; factionAccess: boolean; status: string; note: string | null } | null;
  volunteer: { chosenLevel: number; accessLevel: number; limitPerMin: number; status: string; problem: string | null; calls: number; botUse: boolean; source: string } | null;
  removed?: string[];
  /** Your faction's key pool, numbers only (never whose keys). */
  pool?: (FactionPoolView & { factionId: number }) | null;
}

const LEVELS = ['', 'Public', 'Minimal', 'Limited', 'Full'];

/**
 * Torn's terms table, short (owner's calls 2026-10-31: no "massive 6k character wall of text"; "a short summary … Read more
 * about it in the full terms here"): Torn's five rows with Torn's own answers in bold, word for word (Torn's rule wherever
 * a key is given), our one-sentence summary and "Read more" → the row on /terms; then the link to the full terms. Links
 * open a new tab, so a half-filled form stays.
 */
export function termsTable(fullLabel: string = TERMS_FULL_LABEL): HTMLElement {
  const link = (href: string, text: string, cls: string) => h('a', { href, target: '_blank', rel: 'noopener', class: cls }, text);
  return h(
    'div',
    { class: 'terms-short' },
    h('div', { class: 'table-wrap' }, h('table', { class: 'disclosure' }, h('tbody', {}, ...TORN_TERMS.map((r) => h('tr', { 'data-row': termsAnchor(r.name) }, h('th', { scope: 'row' }, r.name), h('td', {}, h('strong', {}, r.torn.join(' · ')), ' — ', r.summary, ' ', link(`/terms#${termsAnchor(r.name)}`, TERMS_READ_MORE, 'terms-more'))))))),
    h('p', { class: 'terms-full' }, link('/terms', fullLabel, 'terms-full-link')),
  );
}

/** Torn's terms table whole: every detail, word for word, each row with its anchor (/terms#data-storage …). */
export function termsFullTable(anchors = false): HTMLElement {
  return h('div', { class: 'table-wrap' }, h('table', { class: 'disclosure disclosure-full' }, h('tbody', {}, ...TORN_TERMS.map((r) => h('tr', { ...(anchors ? { id: termsAnchor(r.name) } : {}), 'data-row': termsAnchor(r.name) }, h('th', { scope: 'row' }, r.name), h('td', {}, h('strong', {}, r.torn.join(' · ')), ' — ', r.detail))))));
}

export function keysBox(): HTMLElement {
  const status = h('ul', { class: 'plain-list keys-status', role: 'status' }, h('li', { class: 'muted' }, 'Loading…'));
  const msg = h('p', { class: 'form-status', role: 'status' });
  const other = h('input', { type: 'password', autocomplete: 'off', maxlength: '16', placeholder: 'Optional: another key of yours', 'aria-label': 'Another key of yours' }) as HTMLInputElement;
  // One agreement, both uses (owner's call 2026-11-02, terms 17): one button, no per-use switches.
  const agree = h('button', { type: 'button', class: 'btn-primary keys-agree' }, 'Agree and use my key');
  const codeBtn = h('button', { type: 'button', class: 'btn-small keys-code' }, 'Make a code for another browser');
  const codeOut = h('p', { class: 'form-status keys-code-out', role: 'status' });
  const removeAll = h('button', { type: 'button', class: 'btn-danger keys-remove' }, 'Remove my key everywhere');
  const li = (label: string, text: string, ok: boolean | null) => h('li', {}, h('strong', {}, `${label}: `), h('span', { class: ok === null ? '' : ok ? 't-green' : 't-red' }, text));
  const show = (r: KeysInfo) => {
    const a = r.account;
    const f = r.faction;
    status.replaceChildren(
      li('Your account', a.tracked ? `kept on the server (${LEVELS[a.accessLevel ?? 0] ?? '?'} key) — background tracking, and codes for other browsers` : a.problem ? a.problem : !a.on ? 'off (background tracking is off, below)' : 'not kept yet', a.tracked ? true : null),
      li('Your faction', f ? (f.status === 'ok' ? (f.note ?? 'on') : (f.note ?? 'can’t be used')) : 'off', f ? f.status === 'ok' && f.factionAccess : null),
      ...(r.pool ? [h('li', { 'data-field': 'faction-pool' }, h('strong', {}, 'Your faction’s keys: '), h('span', {}, poolText(r.pool)))] : []),
    );
  };
  const load = () => server<KeysInfo>('GET', '/api/me/keys').then(show, (err: Error) => status.replaceChildren(h('li', { class: 't-red' }, err.message)));
  const keyFor = () => {
    const typed = other.value.trim();
    if (typed) return typed;
    const s = session.get();
    // The key typed on this site (the server keeps none for you yet); with the extension it never comes here.
    return s?.mode === 'key' ? s.key : undefined;
  };
  const run = async (body: Record<string, unknown> | null, working: string, done: string) => {
    msg.className = 'form-status';
    msg.textContent = working;
    try {
      const r = body ? await server<KeysInfo>('PUT', '/api/me/keys', { seen: 'terms', ...body }) : await server<KeysInfo>('DELETE', '/api/me/keys');
      other.value = '';
      show(r);
      msg.className = 'form-status ok';
      msg.textContent = done;
    } catch (err) {
      msg.className = 'form-status bad';
      msg.textContent = (err as Error).message;
    }
  };
  const withKey = async (b: Record<string, unknown>) => {
    const kept = await server<KeysInfo>('GET', '/api/me/keys').catch(() => null);
    const k = other.value.trim() || (kept?.account.tracked ? undefined : keyFor());
    return k ? { ...b, key: k } : b;
  };
  agree.addEventListener('click', async () => run(await withKey({ both: true, terms: TERMS_VERSION }), 'Checking with Torn…', 'Your key is used for your account and your faction.'));
  codeBtn.addEventListener('click', async () => {
    try {
      const r = await server<{ code: string; until: number }>('POST', '/api/session/code');
      codeOut.className = 'form-status ok keys-code-out';
      codeOut.replaceChildren('Your code: ', h('strong', { class: 'code' }, showConnectCode(r.code)), ' — type it on the other browser’s sign-in page (this site, or the extension’s options) within 10 minutes. It works once and signs in as you: don’t share it.');
    } catch (err) {
      codeOut.className = 'form-status bad keys-code-out';
      codeOut.textContent = (err as Error).message;
    }
  });
  let armed = false;
  removeAll.addEventListener('click', async () => {
    if (!armed) {
      armed = true;
      removeAll.textContent = 'Click again to remove it everywhere';
      setTimeout(() => ((armed = false), (removeAll.textContent = 'Remove my key everywhere')), 5000);
      return;
    }
    await run(null, 'Removing…', 'Removed everywhere on Omni-Torn’s side (tracking is off now). Deleting the key on Torn stops it for every tool at once.');
  });
  const use = (id: 'account' | 'faction') => KEY_USES.find((u) => u.id === id)!;
  void load();
  return box(
    'Your key in Omni-Torn',
    [
      h('p', { class: 'muted' }, 'One key for this site, the extension and the Discord bot (/account): a key given in one is used by the others, for both uses below — one agreement, the same everywhere. ', KEY_USES_LIMIT_NOTE),
      h('details', { class: 'terms', open: true }, h('summary', {}, 'What Omni-Torn does with your key (Torn’s API terms)'), termsTable()),
      status,
      h('h3', {}, 'What your key is used for'),
      h('ul', { class: 'plain-list keys-uses' }, ...(['account', 'faction'] as const).map((id) => h('li', {}, h('strong', {}, `${use(id).name}: `), use(id).what))),
      h('p', {}, agree),
      h('p', {}, other),
      h('h3', {}, 'What Omni-Torn reads for your account, by key level'),
      h('ul', { class: 'plain-list' }, ...ACCOUNT_LEVELS.map((l) => h('li', {}, h('strong', {}, `${l.name}: `), l.reads))),
      msg,
      h('h3', {}, 'Another browser'),
      h('p', { class: 'muted' }, 'A code signs another browser (or the extension) in as you, with the key Omni-Torn keeps — no typing the key there.'),
      h('p', {}, codeBtn),
      codeOut,
      h('h3', {}, 'Remove it'),
      h('p', {}, removeAll),
    ],
    { cls: 'keys-box', id: 'keys' },
  );
}
